import React from 'react';
import {
    getSignOffGroups,
    resolveQuickReplyVariant,
    pickAvailableGreeting,
    computeQuickReplyTimeOfDay,
    buildQuickReplyContext,
    stripBlankContextValues,
    splitQuickReplyPreview,
    validateQuickReplyAnswer,
    GREETINGS,
    GREETING_KEYS,
    DEFAULT_GREETING,
    DEFAULT_SIGN_OFF,
} from 'src/components/whatsappComponents/quickReplyUtils';
import { getTemplateBody } from 'src/components/whatsappComponents/whatsAppTemplateUtils';

// Build the staff-facing greeting chip label for a greeting config entry,
// interpolating the name / time-of-day where the opener uses it.
const greetingLabel = (greeting, firstName, timeOfDay) => {
    if (greeting.slot === 'name') return `${greeting.emoji} ${greeting.word} ${firstName || 'there'}`;
    if (greeting.slot === 'time') return `${greeting.emoji} ${greeting.word} ${timeOfDay}`;
    return `${greeting.emoji} ${greeting.word}`;
};

/**
 * Drives the closed-window "Personal Reply" flow that reuses the native message
 * input. Staff make two picks — a GREETING (Hi {name} / Good {time} / Thanks for
 * waiting) and a SIGN-OFF (6 options) — and the text typed in the native input is
 * the personalised message (the template's {{answer}}).
 *
 * Templates follow quick_reply_<signoff>_<greeting>; the typed text is owned by
 * ChatPage (`sendMessage`) and passed in as `answer`. This hook owns the two
 * picks and derives the resolved template, the greeting/sign-off options, the
 * live draft-bubble preview, validation, and the send-context builder.
 *
 * @param {object}   opts
 * @param {Array}    opts.templates    - filtered WhatsApp template list
 * @param {object}   opts.leadDetails  - current lead detail object
 * @param {string}   opts.practiceName - practice display name
 * @param {string}   opts.answer       - the typed message (native input text)
 * @returns {object} closed-window Personal Reply state + helpers
 */
export default function usePersonalReply({ templates, leadDetails, practiceName, answer }) {
    const [signOff, setSignOff] = React.useState(DEFAULT_SIGN_OFF);
    const [greeting, setGreeting] = React.useState(DEFAULT_GREETING);

    // One entry per configured sign-off, each with its {hi, tod, pat} template
    // group (or null when that sign-off isn't approved yet).
    const signOffOptions = React.useMemo(() => getSignOffGroups(templates), [templates]);
    const availableKeys = signOffOptions.filter((o) => o.group).map((o) => o.key);
    const hasOptions = availableKeys.length > 0;

    // Effective (always-valid) sign-off — the staff pick when available, else the
    // first available, so a shifting template set can't strand the selection.
    const activeSignOff = availableKeys.includes(signOff) ? signOff : (availableKeys[0] ?? null);
    const selectedGroup = signOffOptions.find((o) => o.key === activeSignOff)?.group || null;

    // Which greetings have an approved template for the selected sign-off.
    const availableGreetings = GREETING_KEYS.filter((key) => selectedGroup?.[key]);
    // Effective greeting — ALWAYS an APPROVED one: keep the staff pick when it's
    // approved for this sign-off, else fall back to the first approved in
    // preference order (tod → hi → pat). Never resolves to a disabled greeting,
    // and re-resolves automatically when the sign-off switches. Falls back to
    // DEFAULT_GREETING only if the sign-off has NO approved greeting (unsendable).
    const activeGreeting = pickAvailableGreeting(selectedGroup, greeting) ?? DEFAULT_GREETING;

    // Concrete template to preview/send = active sign-off group + active greeting.
    // activeGreeting is always approved, so this is always an approved template.
    const selectedTemplate = resolveQuickReplyVariant(selectedGroup, activeGreeting);
    // Retained for reference/tests: true when a sign-off has ≥2 approved greetings.
    // No longer gates the chip row — the row ALWAYS renders (all 3 chips, with
    // unapproved ones disabled) for a consistent UX across sign-offs.
    const canToggleGreeting = availableGreetings.length >= 2;

    const firstNameHint = leadDetails?.first_name || '';
    // Only the treatment name; never the raw treatment object (would stringify to
    // "[object Object]" in the sent context).
    const treatmentHint = leadDetails?.lead_treatments?.[0]?.treatment?.name || '';
    const practiceNameHint = practiceName || '';

    // PREVIEW ONLY time-of-day — lets the `tod` variant read "Good evening, …" in
    // the draft. Never sent (the BE fills the authoritative Europe/London value).
    const previewTimeOfDay = computeQuickReplyTimeOfDay();

    // The three greeting chip options (all rendered; unavailable ones disabled).
    const greetingOptions = GREETINGS.map((g) => ({
        key: g.key,
        label: greetingLabel(g, firstNameHint, previewTimeOfDay),
        available: Boolean(selectedGroup?.[g.key]),
    }));

    // Preview context includes the preview-only time_of_day; the SENT context
    // (buildSendContext) omits it on purpose.
    const context = React.useMemo(
        () =>
            buildQuickReplyContext({
                firstNameHint,
                treatmentHint,
                practiceNameHint,
                answer,
                timeOfDay: previewTimeOfDay,
            }),
        [firstNameHint, treatmentHint, practiceNameHint, answer, previewTimeOfDay]
    );

    const bodyMapping = selectedTemplate?.template_context_mapping?.BODY;
    const previewParts = React.useMemo(
        () => splitQuickReplyPreview(getTemplateBody(selectedTemplate), context, bodyMapping),
        [selectedTemplate, context, bodyMapping]
    );

    const answerError = validateQuickReplyAnswer(answer);

    // SENT context = the 4 authoritative fields only; the server fills time_of_day.
    // Stripped of blanks (same fix as useMappedTemplateDraft — see its
    // sendContext comment): this is passed as `extraContext` to
    // sendWhatsappMessage, and buildWhatsappSendContext (whatsappV2.js)
    // spreads extraContext LAST and unconditionally on top of the FRESH
    // practice/lead context it fetches at click time. A blank hint here
    // (e.g. practiceName before Redux has loaded it) would otherwise
    // overwrite that fresh, correct value with ''. `answer` is exempt — the
    // agent's typed message is exempt from stripping via
    // CONTEXT_KEYS_NEVER_STRIPPED, though `validateQuickReplyAnswer` already
    // keeps Send disabled while it's blank in practice.
    const buildSendContext = React.useCallback(
        () => stripBlankContextValues(
            buildQuickReplyContext({ firstNameHint, treatmentHint, practiceNameHint, answer })
        ),
        [firstNameHint, treatmentHint, practiceNameHint, answer]
    );

    // Reset the two picks (called on a successful send).
    const reset = React.useCallback(() => {
        setSignOff(DEFAULT_SIGN_OFF);
        setGreeting(DEFAULT_GREETING);
    }, []);

    // Live draft-bubble preview, or null when there's no resolved template.
    const preview =
        hasOptions && selectedTemplate
            ? {
                  before: previewParts.before,
                  answer: previewParts.answer,
                  after: previewParts.after,
                  hasAnswer: Boolean(answer.trim()),
                  hasAnswerSlot: previewParts.hasAnswerSlot,
              }
            : null;

    return {
        hasOptions,
        signOffOptions,
        activeSignOff,
        setSignOff,
        greetingOptions,
        activeGreeting,
        setGreeting,
        canToggleGreeting,
        selectedTemplate,
        answerError,
        buildSendContext,
        reset,
        preview,
    };
}
